import React from 'react';
import { reactFormatter } from 'react-tabulator';
import { Avatar } from 'primereact/avatar';
import { DateTime } from 'luxon';
import { dateUserHeaderFilter } from './dateUserHeaderFilter';
import { tabulatorDateUserColumnMinWidth } from './tabulatorColumnMinWidth';
import { buildTabulatorIdColumn } from './tabulatorIdColumn';
import { formatDate as formatDatePattern } from './dateUtils';
import { ensureMandatoryIdVisible } from './maintenanceTabulatorMaskActions';
import { uiT } from './uiTranslate';

/**
 * Standard 1.1 audit-table minimum row height (px).
 * Avatar 2rem (~32px) + 7px top + 7px bottom cell padding = 46.
 */
export const TABULATOR_AUDIT_MIN_ROW_HEIGHT = 46;

export const TABULATOR_AUDIT_PROFILES = Object.freeze({
  createdByUpdatedBy: Object.freeze({
    id: 'createdByUpdatedBy',
    createdUserKeys: Object.freeze(['created_by_user', 'createdByUser']),
    updatedUserKeys: Object.freeze(['updated_by_user', 'updatedByUser']),
  }),
  /** service_tasks: Založenie = created_at + contractor; Posledná aktivita = updated_at + last_touched_by_user */
  contractorLastTouched: Object.freeze({
    id: 'contractorLastTouched',
    createdUserKeys: Object.freeze(['contractor']),
    updatedUserKeys: Object.freeze(['last_touched_by_user', 'lastTouchedByUser']),
  }),
  /** checklist_records: Založenie = created_at + user; Posledná aktivita = updated_at + last_touched_by_user (bez avatara ak null) */
  checklistCreatorLastTouched: Object.freeze({
    id: 'checklistCreatorLastTouched',
    createdUserKeys: Object.freeze(['user']),
    updatedUserKeys: Object.freeze(['last_touched_by_user', 'lastTouchedByUser']),
  }),
});

/**
 * @param {object} rowData
 * @param {typeof TABULATOR_AUDIT_PROFILES.createdByUpdatedBy} profile
 * @param {'created'|'updated'} kind
 */
export function resolveAuditUser(rowData, profile, kind) {
  const keys = kind === 'created' ? profile.createdUserKeys : profile.updatedUserKeys;
  for (const key of keys) {
    if (rowData?.[key]) {
      return rowData[key];
    }
  }
  return null;
}

/** ISO / SQL / JS Date — rovnaká logika ako `formatDate` v dateUtils. */
export function parseAuditDateTime(value) {
  if (value == null || value === '') {
    return null;
  }
  const s = String(value);
  let dt = DateTime.fromISO(s);
  if (!dt.isValid) {
    dt = DateTime.fromSQL(s);
  }
  if (!dt.isValid) {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
      dt = DateTime.fromJSDate(d);
    }
  }
  return dt.isValid ? dt : null;
}

function getAuditTimestampFromRow(rowData, field) {
  if (!rowData) {
    return null;
  }
  if (field === 'created_at') {
    return rowData.created_at ?? rowData.createdAt ?? null;
  }
  return rowData.updated_at ?? rowData.updatedAt ?? null;
}

function buildAuditDateSearchVariants(dt, dateFormat, dateTimeFormat) {
  if (!dt) {
    return '';
  }
  return [dt.toFormat(dateFormat), dt.toFormat(dateTimeFormat), dt.toFormat('yyyy-MM-dd')].join(' ');
}

function normalizeAuditFilterText(str) {
  return (str || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/** Child riadok v strome scheduled (record); parent = karta (scheduled). */
export function isMaintenanceScheduledTreeChildRow(rowData) {
  const parentRef = rowData?.machine_card_id ?? rowData?.maintenance_scheduled_id;
  return parentRef != null && Number(parentRef) > 0;
}

/** Audit user z riadku — parent = maintenance_scheduleds, child = maintenance_scheduled_records. */
export function resolveAuditUserForScheduledTreeRow(rowData, profile, kind) {
  return resolveAuditUser(rowData, profile, kind);
}

export function createAuditDateUserFormatter({
  profile = TABULATOR_AUDIT_PROFILES.createdByUpdatedBy,
  kind,
  dateFormat,
  getUserColor,
  getUserInitials,
  resolveUserFn,
  auditSourceFn,
}) {
  const dateField = kind === 'created' ? 'created_at' : 'updated_at';

  function AuditDateUserFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const source = auditSourceFn ? auditSourceFn(rowData) : rowData;
    const user = resolveUserFn
      ? resolveUserFn(source, profile, kind)
      : resolveAuditUser(source, profile, kind);
    const val = getAuditTimestampFromRow(source, dateField);
    const displayName = user?.employee?.name || user?.username || '';
    const text = val ? formatDatePattern(val, dateFormat, true) : '';

    return (
      <div className="flex items-center justify-start w-full h-full min-w-0 gap-1">
        {user && (
          <Avatar
            className="shrink-0"
            title={displayName}
            label={getUserInitials?.(displayName) || ''}
            shape="circle"
            style={{
              backgroundColor: getUserColor?.(user.username) || '#6b7280',
              color: '#ffffff',
              width: '2rem',
              height: '2rem',
              minWidth: '2rem',
              minHeight: '2rem',
              flexShrink: 0,
              alignSelf: 'center',
              aspectRatio: '1 / 1',
            }}
          />
        )}
        <div className="min-w-0 flex-1 truncate whitespace-nowrap" title={text || undefined}>
          {text}
        </div>
      </div>
    );
  }

  return AuditDateUserFormatter;
}

export function auditRowMatchesSearch(
  rowData,
  rowValue,
  field,
  kind,
  search,
  profile,
  dateFormat,
  dateTimeFormat,
  resolveUserFn
) {
  const resolveUser = resolveUserFn
    ? (rd, k) => resolveUserFn(rd, profile, k)
    : (rd, k) => resolveAuditUser(rd, profile, k);
  const user = resolveUser(rowData, kind);
  const userName = user?.employee?.name || user?.username || '';
  const raw = getAuditTimestampFromRow(rowData, field) ?? rowValue;
  const dt = parseAuditDateTime(raw);
  const dateVariants = buildAuditDateSearchVariants(dt, dateFormat, dateTimeFormat);
  const haystack = `${userName} ${dateVariants}`.trim();
  if (!haystack) {
    return false;
  }
  const needle = normalizeAuditFilterText(search);
  if (!needle) {
    return false;
  }
  return normalizeAuditFilterText(haystack).includes(needle);
}

/** Lokálny `range:YYYY-MM-DD:YYYY-MM-DD` nad `created_at`/`updated_at` daného riadku (nie nadradeného certifikátu). */
export function auditRowMatchesDateRange(rowData, rowValue, field, rangeValue) {
  const rawRange = String(rangeValue ?? '');
  if (!rawRange.startsWith('range:')) {
    return true;
  }
  const parts = rawRange.split(':');
  if (parts.length < 3 || parts[1] === '' || parts[2] === '') {
    return true;
  }
  const from = DateTime.fromISO(parts[1]).startOf('day');
  const to = DateTime.fromISO(parts[2]).endOf('day');
  if (!from.isValid || !to.isValid) {
    return true;
  }
  const raw = getAuditTimestampFromRow(rowData, field) ?? rowValue;
  const dt = parseAuditDateTime(raw);
  if (!dt) {
    return false;
  }
  return dt >= from && dt <= to;
}

/**
 * Lokálny header filter; remote/progressive tabuľky (`remotePassthrough`) nechajú range aj text na BE.
 */
export function createAuditDateUserHeaderFilterFunc({
  profile = TABULATOR_AUDIT_PROFILES.createdByUpdatedBy,
  dateFormat,
  dateTimeFormat,
  resolveUserFn,
  auditSourceFn,
  remotePassthrough = false,
}) {
  return function auditDateUserHeaderFilterFunc(headerValue, rowValue, rowData, filterParams) {
    if (!headerValue || String(headerValue).trim() === '') {
      return true;
    }
    const name = filterParams?.name;
    const field = name === 'created_at' ? 'created_at' : 'updated_at';
    const source = auditSourceFn ? auditSourceFn(rowData) : rowData;
    const sourceValue = source?.[field] ?? rowValue;
    if (String(headerValue).startsWith('range:')) {
      if (remotePassthrough) {
        return true;
      }
      return auditRowMatchesDateRange(source, sourceValue, field, headerValue);
    }
    if (remotePassthrough) {
      return true;
    }

    const search = normalizeAuditFilterText(headerValue);
    const kind = name === 'created_at' ? 'created' : 'updated';
    const matchCtx = [field, kind, search, profile, dateFormat, dateTimeFormat, resolveUserFn];

    if (auditRowMatchesSearch(source, sourceValue, ...matchCtx)) {
      return true;
    }

    return false;
  };
}

/** Do uloženého column layoutu doplní chýbajúce audit polia na koniec — bez zmeny poradia/šírok existujúcich stĺpcov. */
export function appendAuditFieldsToColumnLayout(savedAttributes, fields = ['created_at', 'updated_at']) {
  if (!Array.isArray(savedAttributes) || savedAttributes.length <= 1) {
    return savedAttributes;
  }
  const present = new Set();
  const collect = (nodes) => {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
      if (!node || typeof node !== 'object') continue;
      if (typeof node.field === 'string' && node.field) present.add(node.field);
      if (Array.isArray(node.columns)) collect(node.columns);
    }
  };
  collect(savedAttributes);
  const extra = (fields || [])
    .filter((f) => typeof f === 'string' && f && !present.has(f))
    .map((field) => ({ field }));
  return extra.length ? [...savedAttributes, ...extra] : savedAttributes;
}

/**
 * @param {object} opts
 * @param {typeof TABULATOR_AUDIT_PROFILES.createdByUpdatedBy} [opts.profile]
 * @param {boolean} [opts.includeId]
 * @param {string} opts.dateFormat
 * @param {string} opts.dateTimeFormat
 * @param {Function} [opts.getUserColor]
 * @param {Function} [opts.getUserInitials]
 * @param {Function} [opts.textHeaderFilter] — pre stĺpec ID (remote)
 * @param {object} [opts.idColumnOptions] — buildTabulatorIdColumn opts (frozen, headerFilter, headerFilterFunc, …)
 */
export function buildAuditTabulatorColumns({
  profile = TABULATOR_AUDIT_PROFILES.createdByUpdatedBy,
  includeId = true,
  dateFormat,
  dateTimeFormat,
  getUserColor,
  getUserInitials,
  textHeaderFilter,
  scheduledTree = false,
  remotePassthrough = false,
  auditSourceFn,
  idColumnOptions = {},
}) {
  const resolveUserFn = scheduledTree
    ? (rowData, p, kind) => resolveAuditUserForScheduledTreeRow(rowData, p, kind)
    : undefined;
  const headerFilterFunc = createAuditDateUserHeaderFilterFunc({
    profile,
    dateFormat,
    dateTimeFormat,
    resolveUserFn,
    auditSourceFn,
    remotePassthrough: scheduledTree || remotePassthrough,
  });
  const CreatedFormatter = createAuditDateUserFormatter({
    profile,
    kind: 'created',
    dateFormat,
    getUserColor,
    getUserInitials,
    resolveUserFn,
    auditSourceFn,
  });
  const UpdatedFormatter = createAuditDateUserFormatter({
    profile,
    kind: 'updated',
    dateFormat,
    getUserColor,
    getUserInitials,
    resolveUserFn,
    auditSourceFn,
  });

  const cols = [];

  if (includeId) {
    const {
      frozen = false,
      headerFilter,
      headerFilterFunc: idHeaderFilterFunc,
      headerFilterFuncParams,
      extra,
    } = idColumnOptions;

    cols.push(
      buildTabulatorIdColumn({
        frozen,
        ...(headerFilter !== undefined ? { headerFilter } : {}),
        headerFilterFunc: idHeaderFilterFunc ?? textHeaderFilter,
        ...(headerFilterFuncParams !== undefined ? { headerFilterFuncParams } : {}),
        ...(extra !== undefined ? { extra } : {}),
      })
    );
  }

  const createdTitle = uiT('Common.Column.CreatedAt');
  const updatedTitle = uiT('Common.Column.UpdatedAt');
  cols.push(
    {
      title: createdTitle,
      field: 'created_at',
      minWidth: tabulatorDateUserColumnMinWidth(createdTitle),
      formatter: reactFormatter(<CreatedFormatter />),
      headerFilter: dateUserHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterParams: { dateFormat, dateTimeFormat },
      headerFilterFunc,
      headerFilterFuncParams: { name: 'created_at' },
      hozAlign: 'left',
      sorter: 'datetime',
      sorterParams: { format: 'iso' },
    },
    {
      title: updatedTitle,
      field: 'updated_at',
      minWidth: tabulatorDateUserColumnMinWidth(updatedTitle),
      formatter: reactFormatter(<UpdatedFormatter />),
      headerFilter: dateUserHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterParams: { dateFormat, dateTimeFormat },
      headerFilterFunc,
      headerFilterFuncParams: { name: 'updated_at' },
      hozAlign: 'left',
      sorter: 'datetime',
      sorterParams: { format: 'iso' },
    }
  );

  return cols;
}

/** Audit stĺpce pre maintenance scheduled tree (mntn020/022/023). */
export function buildScheduledTreeAuditTabulatorColumns(opts) {
  return buildAuditTabulatorColumns({ ...opts, scheduledTree: true });
}

/** Zlúčenie viditeľnosti audit stĺpcov do existujúceho default objektu. */
export function mergeColumnVisibilityWithAuditDefaults(defaultVisibility, saved) {
  const auditKeys = ['id', 'created_at', 'updated_at'];
  if (!saved || typeof saved !== 'object') {
    return { ...defaultVisibility };
  }

  const modernMask = Object.prototype.hasOwnProperty.call(saved, 'id');
  const merged = {};

  for (const key of Object.keys(defaultVisibility)) {
    if (Object.prototype.hasOwnProperty.call(saved, key)) {
      merged[key] = saved[key] !== false;
    } else if (auditKeys.includes(key)) {
      merged[key] = defaultVisibility[key];
    } else {
      merged[key] = modernMask ? false : defaultVisibility[key];
    }
  }

  if (!Object.keys(merged).some((k) => merged[k] !== false)) {
    return ensureMandatoryIdVisible({ ...defaultVisibility }, defaultVisibility);
  }
  return ensureMandatoryIdVisible(merged, defaultVisibility);
}

const REMOTE_TREE_AUDIT_FILTER_FIELDS = Object.freeze(['created_at', 'updated_at']);

function isNonEmptyTextAuditFilterValue(headerValue) {
  const trimmed = String(headerValue ?? '').trim();
  return trimmed !== '' && !trimmed.startsWith('range:');
}

/**
 * Aktívny textový audit filter na `created_at` / `updated_at` (nie prázdny, nie `range:`).
 * @param {import('tabulator-tables').Tabulator} table
 */
export function hasActiveRemoteTreeTextAuditFilter(table) {
  if (!table?.getHeaderFilters) {
    return false;
  }
  const seen = new Set();
  for (const entry of table.getHeaderFilters() ?? []) {
    const field = entry?.field;
    if (!REMOTE_TREE_AUDIT_FILTER_FIELDS.includes(field)) {
      continue;
    }
    if (!isNonEmptyTextAuditFilterValue(entry?.value)) {
      continue;
    }
    if (!seen.has(field)) {
      seen.add(field);
      return true;
    }
  }
  for (const field of REMOTE_TREE_AUDIT_FILTER_FIELDS) {
    const headerValue = table.getColumn?.(field)?.getHeaderFilterValue?.();
    if (isNonEmptyTextAuditFilterValue(headerValue)) {
      return true;
    }
  }
  return false;
}

function isRemoteScheduledTreeParentRow(row) {
  if (!row || row.getTreeParent?.()) {
    return false;
  }
  const data = row.getData?.();
  return Array.isArray(data?._children) && data._children.length > 0;
}

function collectRemoteScheduledTreeParentRows(table) {
  let candidateRows = [];
  try {
    const activeRows = table.getRows?.('active');
    if (Array.isArray(activeRows) && activeRows.length > 0) {
      candidateRows = activeRows;
    }
  } catch (_) {
    /* getRows('active') nemusí byť podporované */
  }
  if (candidateRows.length === 0) {
    try {
      const visibleRows = table.getRows?.(true);
      if (Array.isArray(visibleRows) && visibleRows.length > 0) {
        candidateRows = visibleRows;
      }
    } catch (_) {}
  }
  if (candidateRows.length === 0) {
    candidateRows = table.getRows?.() ?? [];
  }

  const parentRows = candidateRows.filter(isRemoteScheduledTreeParentRow);
  if (parentRows.length > 0) {
    return parentRows;
  }

  const fromData = table.getData?.() ?? [];
  const byId = new Map();
  for (const item of fromData) {
    if (!Array.isArray(item?._children) || item._children.length === 0) {
      continue;
    }
    const row = table.getRow?.(item.id);
    if (row && isRemoteScheduledTreeParentRow(row)) {
      byId.set(item.id, row);
    }
  }
  return [...byId.values()];
}

/**
 * Remote scheduled tree (mntn020/022/023): po načítaní rozbaľ parent riadky s `_children`,
 * ak je aktívny textový audit filter.
 * @param {import('tabulator-tables').Tabulator} table
 */
export function expandRemoteTreeRowsForActiveAuditFilter(table) {
  if (!table || !hasActiveRemoteTreeTextAuditFilter(table)) {
    return;
  }
  const parentRows = collectRemoteScheduledTreeParentRows(table);
  for (const row of parentRows) {
    if (!row.isTreeExpanded?.()) {
      row.treeExpand?.();
    }
  }
}

/**
 * Naplánuje expand po dokončení renderu (chránené ref flagom proti loopu).
 * @param {() => import('tabulator-tables').Tabulator | null | undefined} getTable
 * @param {{ current: boolean }} inProgressRef
 * @param {{ current: ReturnType<typeof setTimeout> | null }} scheduleRef
 */
export function scheduleExpandRemoteTreeRowsForActiveAuditFilter(getTable, inProgressRef, scheduleRef) {
  if (scheduleRef.current != null) {
    clearTimeout(scheduleRef.current);
  }
  scheduleRef.current = setTimeout(() => {
    scheduleRef.current = null;
    const table = typeof getTable === 'function' ? getTable() : getTable;
    if (!table || inProgressRef.current) {
      return;
    }
    if (!hasActiveRemoteTreeTextAuditFilter(table)) {
      return;
    }

    inProgressRef.current = true;
    requestAnimationFrame(() => {
      setTimeout(() => {
        try {
          expandRemoteTreeRowsForActiveAuditFilter(table);
        } finally {
          inProgressRef.current = false;
        }
      }, 50);
    });
  }, 0);
}
